import { compact, isEmpty, map, some } from 'lodash';
import {
  at,
  epoch,
  formatDate,
  formatEpochAsDate,
  formatEpochAsTime,
  tz,
  utc
} from '../../util/datetime';
import { toUUID } from '../../util/transform';
import { isBlank } from '../../util/validate';
import Income from '../finance/Income';
import Household from '../finance/Household';
import Asset from '../finance/Asset';
import Insurance from '../finance/Insurance';
import Liability from '../finance/Liability';
import { DocType } from '../global/Doc.enum';
import Root from '../Root';
import { ContactMaritalStatusOption } from './ContactMaritalStatusOption.enum';
import type { IContact } from './Contact.interface';
import ContactCompany from './ContactCompany';
import ContactCreditScore from './ContactCreditScore';
import ContactPerson from './ContactPerson';
import ContactVerificationScore from './ContactVerificationScore';
import { ContactEmploymentType } from './ContactEmploymentType.enum';
import { ContactCompanyType } from './ContactCompanyType.enum';
import { ContactCompanyTrustStructure } from './ContactCompanyTrustStructure.enum';
import Address from '../address/Address';
import AssetFinanceAssetCommitmentSchedule from '../finance/AssetFinanceAssetCommitmentSchedule';

export default class Contact extends Root implements IContact {
  ref: string;
  avatar: string;
  description: string;
  idLabels: Array<string>;
  person: ContactPerson;
  company: ContactCompany;
  creditScore: Array<ContactCreditScore>;
  verificationScore: ContactVerificationScore;
  income: Array<Income>;
  expenses: Array<Household>;
  assets: Array<Asset>;
  assetCommitmentSchedules: Array<AssetFinanceAssetCommitmentSchedule>;
  liabilities: Array<Liability>;
  insurances: Array<Insurance>;
  isOrphaned: boolean;
  isShared: boolean;
  isCompany: boolean;
  isMarketing: boolean;
  isUpdatesAndReminders: boolean;
  isPrivacyDisclosure: boolean;
  privacyDisclosureTimeStamp: number;

  showDirector() {
    return (
      this.isCompany &&
      (this.company?.information?.typeOfBusinessEntity?.name ===
        ContactCompanyType.trustee_company.name ||
        this.company?.information?.typeOfBusinessEntity?.name ===
          ContactCompanyType.pty_ltd.name ||
        this.company?.information?.typeOfBusinessEntity?.name === ContactCompanyType.ltd.name)
    );
  }

  showBeneficiary() {
    return (
      this.isCompany &&
      this.company?.information?.trustStructure?.name ===
        ContactCompanyTrustStructure.discretionary_trust.name
    );
  }

  showShareholder() {
    return (
      this.isCompany &&
      (this.company.information.typeOfBusinessEntity?.name ===
        ContactCompanyType.trustee_company.name ||
        this.company.information.typeOfBusinessEntity?.name === ContactCompanyType.pty_ltd.name)
    );
  }

  showUnitholder() {
    return (
      this.isCompany &&
      this.company.information.trustStructure?.name !==
        ContactCompanyTrustStructure.none.name &&
      this.company.information.trustStructure?.name !==
        ContactCompanyTrustStructure.discretionary_trust.name
    );
  }

  showEmployee() {
    return (
      this.isCompany &&
      (this.company.information.typeOfBusinessEntity?.name ===
        ContactCompanyType.pty_ltd.name ||
        this.company.information.typeOfBusinessEntity?.name === ContactCompanyType.ltd.name)
    );
  }

  showSecretary() {
    return (
      this.isCompany &&
      (this.company.information.typeOfBusinessEntity?.name ===
        ContactCompanyType.trustee_company.name ||
        this.company.information.typeOfBusinessEntity?.name ===
          ContactCompanyType.pty_ltd.name ||
        this.company.information.typeOfBusinessEntity?.name === ContactCompanyType.ltd.name)
    );
  }

  showOfficeHolder() {
    return (
      this.isCompany &&
      this.company?.information?.typeOfBusinessEntity?.name ===
        ContactCompanyType.association.name
    );
  }

  getDateOfBirth(timeZone, locale, localeFormat) {
    return this.person?.information?.dateOfBirth !== 0
      ? formatDate(
          tz(utc(this.person?.information?.dateOfBirth), timeZone),
          locale,
          localeFormat
        )
      : '-';
  }

  getDateOfBirthForMeCard(timeZone) {
    return this.person?.information?.dateOfBirth !== 0
      ? at(tz(utc(this.person?.information?.dateOfBirth), timeZone)).toFormat('yyyyMMdd')
      : '';
  }

  getDriversLicenseExpiryDate(timeZone, locale, localeFormat) {
    return this.person?.information?.driversLicenseExpiryDate !== 0
      ? formatEpochAsDate(
          this.person?.information?.driversLicenseExpiryDate,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getPassportIssueDate(timeZone, locale, localeFormat) {
    return this.person?.information?.passportIssueDate !== 0
      ? formatEpochAsDate(
          this.person?.information?.passportIssueDate,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getPassportExpiryDate(timeZone, locale, localeFormat) {
    return this.person?.information?.passportExpiryDate !== 0
      ? formatEpochAsDate(
          this.person?.information?.passportExpiryDate,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getMedicareExpiryDate(timeZone, locale, localeFormat) {
    return this.person?.information?.medicareExpiryDate !== 0
      ? formatEpochAsDate(
          this.person?.information?.medicareExpiryDate,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getAddressFrom(timeZone, locale, localeFormat) {
    return this.person?.contact?.from !== 0
      ? formatEpochAsDate(this.person?.contact?.from, timeZone, locale, localeFormat)
      : '-';
  }

  getPreviousAddressFrom(timeZone, locale, localeFormat) {
    return this.person?.contact?.previousFrom !== 0
      ? formatEpochAsDate(this.person?.contact?.previousFrom, timeZone, locale, localeFormat)
      : '-';
  }

  getIncorporated(timeZone, locale, localeFormat) {
    return this.company?.information?.incorporated !== 0
      ? formatEpochAsDate(
          this.company?.information?.incorporated,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getDateOfRegistration(timeZone, locale, localeFormat) {
    return this.company?.information?.dateOfRegistration !== 0
      ? formatEpochAsDate(
          this.company?.information?.dateOfRegistration,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getVatRegisteredDate(timeZone, locale, localeFormat) {
    return this.company?.information?.vatRegisteredDate !== 0
      ? formatEpochAsDate(
          this.company?.information?.vatRegisteredDate,
          timeZone,
          locale,
          localeFormat
        )
      : '-';
  }

  getMeCard(timeZone) {
    return `firstName=${this.getFirstName()}&familyName=${this.getFamilyName()}&phone=${this.getPhone()}&email=${this.getEmail()}&address=${this.getAddress()}&birthday=${this.getDateOfBirthForMeCard(
      timeZone
    )}`;
  }

  getAvatar() {
    return this.avatar;
  }

  getTitle() {
    return `${this.person?.information?.title?.title}`;
  }

  getName() {
    return this.isCompany
      ? this.company?.information?.name
      : `${this.person?.information?.firstName} ${this.person?.information?.familyName}`;
  }

  getNameReversed() {
    return this.isCompany
      ? this.company?.information?.name
      : `${this.person?.information?.familyName} ${this.person?.information?.firstName}`;
  }

  getFirstName() {
    return this.isCompany
      ? this.company?.information?.name
      : `${this.person?.information?.firstName}`;
  }

  getFamilyName() {
    return this.isCompany ? '' : `${this.person?.information?.familyName}`;
  }

  getPhone() {
    return this.isCompany
      ? `${
          this.company?.contact?.phoneCode && this.company?.contact?.phone
            ? `${this.company?.contact?.phoneCode} ${this.company?.contact?.phone}`
            : ''
        }`
      : `${
          this.person?.contact?.primaryCode && this.person?.contact?.primary
            ? `${this.person?.contact?.primaryCode} ${this.person?.contact?.primary}`
            : ''
        }`;
  }

  getEmail() {
    return this.isCompany ? this.company?.contact?.email : this.person?.contact?.email;
  }

  hasAddress() {
    return this.isCompany
      ? Address.hasAddress(this.company?.contact?.address)
      : Address.hasAddress(this.person?.contact?.address);
  }

  getAddress() {
    return this.isCompany
      ? Address.getAddress(this.company?.contact?.address)
      : Address.getAddress(this.person?.contact?.address);
  }

  getEntity() {
    return this.isCompany ? 'Company' : 'Person';
  }

  hasMetClientPortalPrerequisites() {
    return this.getEmail() && this.getPhone();
  }

  hasPropertyAsset({ address = {} }) {
    return some(this.assets, (asset) => address.formatted === asset.address?.formatted);
  }

  hasMortgageLiability(accountNumberSplit) {
    return some(
      this.liabilities,
      ({ accountNumber }) => accountNumber && accountNumberSplit === accountNumber
    );
  }

  getMaritalStatus() {
    const maritalStatusId =
      this.person?.information?.maritalStatus?.name || this.person?.information?.maritalStatus;
    const maritalStatus = ContactMaritalStatusOption.available();
    let status;

    if (maritalStatus && maritalStatus.length) {
      status = maritalStatus.find(
        (m) => m.name === maritalStatusId || m.id === maritalStatusId
      );
    }

    return status ? status.title : 'N/A';
  }

  isDateOfCurrentAddressLessThanThreeYears() {
    if (this.person.contact.from) {
      return (
        parseInt(utc(this.person.contact.from).diffNow('days').toObject().days, 10) > -1095
      );
    }
    return false;
  }

  isCurrentEmployerSelfEmployed() {
    return some(
      this.person.employments,
      (employment) =>
        employment?.isCurrent &&
        employment?.type?.name === ContactEmploymentType.self_employed.name
    );
  }

  getPrivacyDisclosureDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.privacyDisclosureTimeStamp, timeZone, locale, localeFormat);
  }

  getDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.timeStamp, timeZone, locale, localeFormat);
  }

  getTime(timeZone, locale, localeFormat) {
    return formatEpochAsTime(this.timeStamp, timeZone, locale, localeFormat);
  }

  static build = (data?: IContact) => new Contact(data);
  static blank = () =>
    new Contact({ isMarketing: true, isUpdatesAndReminders: true, isActive: true });

  /**
   * Constructor
   * @param {IContact} data
   */
  constructor(data?: IContact) {
    super(DocType.of('__datum.contact'), data);

    this.ref = data?.ref || '';
    this.avatar = data?.avatar || '';
    this.description = data?.description || '';
    this.idLabels = compact(map(data?.idLabels, (obj) => toUUID(obj)));
    this.person = new ContactPerson(data?.person);
    this.company = new ContactCompany(data?.company);
    this.creditScore = map(data?.creditScore, (obj) => new ContactCreditScore(obj));
    this.verificationScore = new ContactVerificationScore(data?.verificationScore);
    this.income = map(data?.income, (obj) => new Income(obj));
    this.expenses = map(compact(data?.expenses), (obj) => new Household(obj));
    this.assets = map(data?.assets, (obj) => new Asset(obj));
    this.assetCommitmentSchedules = compact(
      map(data?.assetCommitmentSchedules, (obj) => new AssetFinanceAssetCommitmentSchedule(obj))
    );
    this.liabilities = map(data?.liabilities, (obj) => new Liability(obj));
    this.insurances = map(data?.insurances, (obj) => new Insurance(obj));
    this.isOrphaned = data?.isOrphaned === true;
    this.isShared = data?.isShared === true;
    this.isCompany = !isBlank(data?.company?.information?.name);
    this.isMarketing = data?.isMarketing === true;
    this.isUpdatesAndReminders = data?.isUpdatesAndReminders === true;
    this.isPrivacyDisclosure = data?.isPrivacyDisclosure === true;
    this.privacyDisclosureTimeStamp = data?.privacyDisclosureTimeStamp
      ? epoch(utc(data?.privacyDisclosureTimeStamp))
      : 0;
  }
}
